/**
 * 学生端「我的积分」（见契约 2.2，儿童模式友好）。
 *
 * 孩子对积分与流水**完全可见**（已确认的裁定），所以这一页不做任何折叠或隐藏，
 * 而且要让每一分的来源都能被读懂：原因图标 + 中文原因 + 备注 + 时间 + 变化后余额。
 *
 * 儿童模式（小学 1~3）：超大余额、大字号流水、图标+文字并列、"还差 X 分"也是大号；
 * 并且**隐藏统计图表**（见方案 2.5）—— 这一页只有"我攒了多少、怎么攒的"。
 *
 * id 空间：页面内的 studentId 必须是 **student.id**，由 `useMyStudent()` 从
 * auth_user.id 翻译而来（见 lib/useMyStudent.ts 的说明）。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Card, CardHeader, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState, NotReadyState } from '../../components/ui/Bits';
import { PointsLedgerList } from '../../components/PointsLedgerList';
import { useLedger, usePointsSummary, useStudentRewardList } from '../../lib/domainQueries';
import { useMyStudent } from '../../lib/useMyStudent';
import { resolveUiMode, uiModeHint } from '../../lib/uiMode';
import { toErrorMessage } from '../../lib/api/error';
import { cn } from '../../lib/utils';

const PAGE_SIZE = 20;

export function StudentPointsPage() {
  const my = useMyStudent();

  if (my.isLoading) {
    return (
      <AppShell kind="student" title="我的积分">
        <Skeleton className="h-40 w-full" />
      </AppShell>
    );
  }
  if (!my.studentId) {
    return (
      <AppShell kind="student" title="我的积分">
        <ErrorState
          message={toErrorMessage(my.error) || '没能确认你的学生档案，请稍后重试'}
          onRetry={my.refetch}
        />
      </AppShell>
    );
  }
  return <StudentPointsScreen studentId={my.studentId} />;
}

function StudentPointsScreen({
  studentId,
}: {
  studentId: string;
}) {
  const [page, setPage] = useState(1);

  const pointsQuery = usePointsSummary(studentId);
  const ledgerQuery = useLedger(studentId, page, 'all');
  /** 用最便宜的奖品来回答"还差多少分就能换东西"——这是激励的关键（契约 2.2）。 */
  const rewardsQuery = useStudentRewardList(studentId);

  const mode = resolveUiMode({ serverUiMode: null });
  const kid = mode === 'child';

  const balance = pointsQuery.data?.balance ?? 0;
  const notReady = pointsQuery.data?.notReady ?? false;
  const total = ledgerQuery.data?.total ?? 0;
  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /** 最近的可兑换目标：比余额高一点点的那一个，最能驱动孩子继续打卡。 */
  const nextReward = (() => {
    const rewards = (rewardsQuery.data?.rewards ?? [])
      .filter((reward) => reward.active && (reward.stock === null || reward.stock > 0))
      .slice()
      .sort((a, b) => a.costPoints - b.costPoints);
    return rewards.find((reward) => reward.costPoints > balance) ?? null;
  })();

  return (
    <AppShell
      kind="student"
      title="我的积分"
      compactHeader={kid}
    >
      <div className={cn('space-y-5', kid && 'ui-child space-y-6')}>
        {/* ---------- 余额（醒目） ---------- */}
        <section
          aria-label="积分余额"
          className={cn(
            'rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white',
            kid ? 'p-6 text-center' : 'p-5',
          )}
        >
          <p className={cn(kid ? 'text-xl' : 'text-sm')}>我的积分</p>
          {pointsQuery.isLoading ? (
            <Skeleton className="mx-auto mt-2 h-12 w-32 bg-white/40" />
          ) : (
            <p className={cn('font-bold tabular-nums', kid ? 'text-6xl' : 'text-4xl')}>
              {notReady ? '—' : balance}
            </p>
          )}
          <p className={cn('mt-2 text-amber-50', kid ? 'text-base' : 'text-xs')}>
            {balance < 0
              ? '积分透支了，下次打卡先补回来就好'
              : '完成打卡就能攒积分，攒够了可以去奖品屋兑换'}
          </p>
        </section>

        {/* ---------- "还差 X 分能换 XXX" ---------- */}
        {!notReady && nextReward && (
          <Card className={cn(kid ? 'border-2 border-amber-200' : '')}>
            <p className={cn('font-medium text-slate-700', kid ? 'text-xl' : 'text-sm')}>
              🎁 还差 <span className="font-bold text-amber-600">{nextReward.costPoints - balance}</span> 分
              就能换「{nextReward.name}」了
            </p>
            <div
              className={cn('mt-3 w-full overflow-hidden rounded-full bg-slate-100', kid ? 'h-5' : 'h-3')}
              role="progressbar"
              aria-valuenow={Math.min(100, Math.round((balance / nextReward.costPoints) * 100))}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`距离兑换 ${nextReward.name} 的进度`}
            >
              <div
                className="h-full rounded-full bg-amber-500"
                style={{
                  width: `${Math.min(100, Math.max(0, Math.round((balance / nextReward.costPoints) * 100)))}%`,
                }}
              />
            </div>
            <p className={cn('mt-2 text-slate-500', kid ? 'text-base' : 'text-xs')}>
              现在 {balance} 分 / 需要 {nextReward.costPoints} 分
            </p>
            <div className="mt-3">
              <Link to="/student/rewards">
                <Button size={kid ? 'kid' : 'sm'} variant={kid ? 'kid' : 'secondary'}>
                  去奖品屋看看
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* ---------- 流水（孩子完全可见） ---------- */}
        <Card className={kid ? 'p-5' : undefined}>
          <CardHeader
            title={kid ? '积分小账本' : '积分流水'}
            description="每一笔加减分都记在这里，可以随时翻看。"
          />

          {pointsQuery.data?.notReady ? (
            <NotReadyState feature="积分" compact />
          ) : ledgerQuery.isError ? (
            <ErrorState
              message={toErrorMessage(ledgerQuery.error)}
              onRetry={() => void ledgerQuery.refetch()}
              compact
            />
          ) : ledgerQuery.data?.notReady ? (
            <NotReadyState feature="积分流水" compact />
          ) : (
            <PointsLedgerList
              entries={ledgerQuery.data?.entries}
              isLoading={ledgerQuery.isLoading}
              kid={kid}
              emptyHint="完成第一个打卡任务后，这里就会出现第一条记录。"
            />
          )}

          {maxPage > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
              <Button
                variant="secondary"
                size={kid ? 'kid' : 'sm'}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </Button>
              <span className={kid ? 'text-lg' : 'text-sm'}>
                {page} / {maxPage}
              </span>
              <Button
                variant="secondary"
                size={kid ? 'kid' : 'sm'}
                disabled={page >= maxPage}
                onClick={() => setPage((p) => p + 1)}
              >
                下一页
              </Button>
            </div>
          )}
        </Card>

        <p className={cn('text-slate-400', kid ? 'text-base' : 'text-xs')}>{uiModeHint(mode)}</p>
      </div>
    </AppShell>
  );
}
